// src/hooks/useDimensions.js
import { useCallback } from "react";
import { debounce } from "lodash";
import { FONTS } from "../constants/fonts";
import { setDimensions, clearDimensionsCache } from "../utils/dimensions";
import { useData } from "./useData";

export const useDimensions = () => {
  const {
    canvas,
    selectedProduct,
    selectedFont,
    selectedSize,
    setGlobalDimensions,
  } = useData();

  const updateDimensions = useCallback(
    debounce(() => {
      console.log("useDimensions: updateDimensions triggered", {
        canvasExists: !!canvas,
        selectedProduct,
        productCode: selectedProduct?.code,
      });

      const effectiveProduct = selectedProduct;
      if (!canvas || !effectiveProduct?.code) {
        setGlobalDimensions?.({
          lines: [],
          isMultiline: false,
          message: "No text",
        });
        return;
      }

      const allTextObjects = canvas
        .getObjects()
        .filter(
          (obj) => obj.type === "i-text" && obj.text && !obj.isPlaceholder
        );

      if (!allTextObjects.length) {
        setGlobalDimensions?.({
          lines: [],
          isMultiline: false,
          message: "No text",
        });
        return;
      }

      const fonts = FONTS || [];
      if (!fonts.length) {
        setGlobalDimensions?.({
          lines: [],
          isMultiline: false,
          message: "No fonts",
        });
        return;
      }

      clearDimensionsCache();

      const lines = allTextObjects.map((obj, index) => {
        const font =
          fonts.find((f) => f.code === (obj.fontFamily || selectedFont)) ||
          fonts[0];
        const sizeCode = obj.size || selectedSize || "02";

        const dims = setDimensions(obj, font.code, sizeCode, fonts, {
          scaleX: obj.scaleX || 1,
          precision: "precise",
          productCode: effectiveProduct.code,
          heightOverride:
            effectiveProduct.sizes.find((s) => s.code === sizeCode)?.height ||
            2.0,
        });

        if (!dims || typeof dims.widthInches === "undefined") {
          return { width: 0, height: 0, canvasIndex: index };
        }

        return {
          width: dims.widthInches,
          height: dims.heightInches,
          canvasIndex: index,
        };
      });

      const computedDimensions = {
        lines: lines.filter((line) => line.width !== undefined),
        isMultiline: allTextObjects.length > 1,
        message: "",
      };

      if (typeof setGlobalDimensions === "function") {
        setGlobalDimensions(computedDimensions);
      } else {
        console.error("setGlobalDimensions is not available");
      }

      console.log("useDimensions: Updated dimensions", computedDimensions);
    }, 100),
    [canvas, selectedProduct, selectedFont, selectedSize, setGlobalDimensions]
  );

  return { updateDimensions };
};

export default useDimensions;
